<template>
<layout-content>
  <template #toolbar>
    <data-time-query :query="query" />

    <el-select v-model="query.siteIds[0]" size="small" style="width: 150px;">
      <el-option
        v-for="item in filterSites"
        :key="item.id"
        :label="item.name"
        :value="item.id">
      </el-option>
    </el-select>

    <completed-query v-model="query.isAuditCompleted" @change="onAuditCompletedChanged" />

    <el-button
      type="success"
      size="small"
      icon="el-icon-download"
      @click="onExportBtnClick"
    >导出</el-button>

    <el-button
      :disabled="canNotSearch"
      type="orange"
      size="small"
      icon="el-icon-search"
      @click="onSearchBtnClick"
    >查询</el-button>
  </template>

  <template #body>
    <data-wrapper>
      <div class="grid-table-wrapper">
        <div class="grid-table-container">
          <table ref="table" class="grid-table">
            <thead>
            <tr>
              <th rowspan="4">时间</th>
              <th :colspan="factorColspan">污染物浓度及空气质量分指数(IAQI)</th>
              <th rowspan="4">空气质量指数(AQI)</th>
              <th rowspan="4">综合指数</th>
              <th rowspan="4">首要污染物</th>
              <th rowspan="4">空气质量<br />指数级别</th>
              <th rowspan="2" colspan="2">空气质量<br/>指数类别</th>
            </tr>
            <tr>
              <th
                v-for="(factorColumn, index) in factorColumns"
                :key="factorColumn.id"
                rowspan="2"
                :colspan="1 + factorColumn.hasIAQI"
                :class="index === 0 ? 'no-border-left no-fixed' : ''"
              >{{factorColumn.name}}<br />{{factorColumn.dataTypeText}}</th>
            </tr>
            <tr>
              <th rowspan="2" class="no-border-left no-fixed">类别</th>
              <th rowspan="2">颜色</th>
            </tr>
            <tr>
              <template v-for="(factorColumn, index) in factorColumns">
                <th
                  :key="`${factorColumn.id}-value`"
                  rowspan="1"
                  :class="index === 0 ? 'no-border-left no-fixed' : ''"
                >浓度<br/>({{factorColumn.unit}})</th>
                <th
                  v-if="factorColumn.hasIAQI"
                  rowspan="1"
                  :key="`${factorColumn.id}-iaqi`"
                >分指数</th>
              </template>
            </tr>
            </thead>

            <tbody class="row-stripe">
            <tr
              v-for="(data, index) in dataList"
              :key="index"
            >
              <td align="center">{{data.dataTime}}</td>

              <template v-for="factorColumn in factorColumns">
                <td :key="`${factorColumn.id}-value`" align="center">{{data[`${factorColumn.id}-value`]}}</td>
                <td v-if="factorColumn.hasIAQI" :key="`${factorColumn.id}-iaqi`" align="center">{{data[`${factorColumn.id}-iaqi`]}}</td>
              </template>

              <td align="center">{{data.aqi}}</td>
              <td align="center">{{data.compositeIndex}}</td>
              <td align="center">{{data.primaryPollutant}}</td>
              <td align="center">{{data.aqiGradeFormat}}</td>
              <td align="center">{{data.aqiGradeType}}</td>
              <td align="center"><div :grade="data.aqiGrade" class="grade-legend-section">{{data.aqiGrade}}</div></td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="border-top"></div>
        <div class="border-bottom"></div>
      </div>
    </data-wrapper>
  </template>
</layout-content>
</template>

<script>
import BaseSiteFactorData from '../../../BaseSiteFactorData'
import LayoutContent from '../../../../components/Layout/Content'
import DataTimeQuery from '../../../../components/Data/Query/DataTimeQuery'
import CompletedQuery from '../../../../components/Data/Query/CompletedQuery'
import DeviceCategoryConfig from '../../../../model/DeviceCategoryConfig'
import DataWrapper from '../../../../components/Data/DataWrapper'
import XLSXS from 'xlsx-js-style'

const AQI_GRADES = process.env.AQI_GRADES

export default {
  name: 'AQIRealtimeReport',
  routeInfo: {
    label: 'AQI实时报',
    icon: 'fa fa-users'
  },
  extends: BaseSiteFactorData,
  components: {DataWrapper, CompletedQuery, DataTimeQuery, LayoutContent},
  computed: {
    siteDeviceCategoryConfig () {
      return [
        new DeviceCategoryConfig(1, true),
        new DeviceCategoryConfig(process.env.SO2_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1001')),
        new DeviceCategoryConfig(process.env.NO_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1003')),
        new DeviceCategoryConfig(process.env.CO_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1005')),
        new DeviceCategoryConfig(process.env.O3_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.reduce((list, siteDeviceFactor) => {
          if (siteDeviceFactor.factorId === '1006') {
            list.push(siteDeviceFactor, {
              ...siteDeviceFactor,
              factorId: '1006-8H'
            })
          }

          return list
        }, [])),
        new DeviceCategoryConfig(process.env.PM_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.reduce((list, siteDeviceFactor) => {
          const match = siteDeviceFactor.factorId.match(/^100(7|8)$/)

          if (match) {
            list.push(siteDeviceFactor, {
              ...siteDeviceFactor,
              factorId: `${match[0]}-24H`
            })
          }

          return list
        }, []))
      ]
    },
    currentSite () {
      return this.filterSiteMap[this.query.siteIds[0]]
    },
    factorColumns: () => [{
      id: '1001',
      rootFactorId: 'SO2',
      name: '二氧化硫(SO₂)',
      dataTypeText: '1小时平均',
      unit: 'μg/m³',
      hasIAQI: true
    }, {
      id: '1003',
      rootFactorId: 'NO2',
      name: '二氧化氮(NO₂)',
      dataTypeText: '1小时平均',
      unit: 'μg/m³',
      hasIAQI: true
    }, {
      id: '1007',
      rootFactorId: 'PM10',
      name: '颗粒物(粒径小于等于10μm)',
      dataTypeText: '1小时平均',
      unit: 'μg/m³',
      hasIAQI: true
    }, {
      id: '1005',
      rootFactorId: 'CO',
      name: '一氧化碳(CO)',
      dataTypeText: '1小时平均',
      unit: 'mg/m³',
      hasIAQI: true
    }, {
      id: '1006',
      rootFactorId: 'O3',
      name: '臭氧(O₃)',
      dataTypeText: '1小时平均',
      unit: 'μg/m³',
      hasIAQI: true
    }, {
      id: '1006-8H',
      rootFactorId: 'O3_8H',
      name: '臭氧(O₃)',
      dataTypeText: '8小时滑动平均',
      unit: 'μg/m³',
      hasIAQI: true
    }, {
      id: '1008',
      rootFactorId: 'PM2.5',
      name: '颗粒物(粒径小于等于2.5μm)',
      dataTypeText: '1小时平均',
      unit: 'μg/m³',
      hasIAQI: true
    }],
    factorColumnMap () {
      return this.factorColumns.reduce((map, factorColumn) => {
        map[factorColumn.rootFactorId] = factorColumn
        return map
      }, {})
    },
    factorColspan () {
      return this.factorColumns.reduce((count, factorColumn) => count + (1 + factorColumn.hasIAQI), 0)
    }
  },
  methods: {
    setCurrentQuery () {
      const {query} = this

      this.currentQuery = Object.freeze({
        beginTime: query.beginTime,
        endTime: query.endTime,
        dataType: 60,
        siteIds: query.siteIds,
        factorIds: query.factorIds,
        granularity: 1,
        isAuditCompleted: query.isAuditCompleted,
        isFuse: query.isFuse,
        isSiteMerge: false,
        isTimeMerge: false
      })
    },
    getData () {
      const {currentQuery} = this
      const {siteIds} = currentQuery

      if (siteIds.length === 0) {
        return
      }

      this.changeLoadStatus(true)

      const promises = [
        this.$api.Aqi.getAQIData(currentQuery),
        this.$api.Aqi.getComposite(currentQuery)
      ]

      Promise.all(promises).then(this.setData).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    },
    setData ([aqiHourlies, compositeIndexHourlies]) {
      const {currentQuery: {siteIds: [siteId], beginTime, endTime}, factorColumns, factorColumnMap} = this
      const factorMap = this.siteFactorMap[siteId].map
      const nullValueMap = factorColumns.reduce((map, factorColumn) => {
        const {id} = factorColumn
        map[id] = factorMap[id] ? '-' : process.env.NA
        return map
      }, {})
      const beginTS = new Date(beginTime).getTime()
      const endTS = new Date(endTime).getTime()
      const dataMap = {}

      for (let i = beginTS; i <= endTS; i += 3600000) {
        const date = new Date(i)
        dataMap[date.format('YYYY/MM/DD HH:mm:ss')] = factorColumns.reduce((data, factorColumn) => {
          const {id} = factorColumn
          const nullValue = nullValueMap[id]

          data[`${id}-value`] = nullValue

          if (factorColumn.hasIAQI) {
            data[`${id}-iaqi`] = nullValue
          }

          return data
        }, {
          dataTime: date.format('YYYY-MM-DD HH'),
          aqi: '-',
          compositeIndex: '-',
          primaryPollutant: '-',
          aqiGrade: '-',
          aqiGradeFormat: '-',
          aqiGradeType: '-'
        })
      }

      aqiHourlies.forEach(data => {
        const newData = dataMap[data.dataTime]
        const {aqi} = data

        if (Number.isFinite(aqi)) {
          const {grade, primaryPollutant} = data
          newData.aqi = Math.round(data.aqi)
          newData.aqiGrade = grade
          newData.aqiGradeFormat = `${grade}级`
          newData.aqiGradeType = AQI_GRADES[grade - 1]
          newData.primaryPollutant = primaryPollutant === '无' ? primaryPollutant : primaryPollutant.split(',').map(rootFactorId => {
            if (rootFactorId === 'H8O3') {
              rootFactorId = 'O3_8H'
            }

            return factorColumnMap[rootFactorId].name
          }).join(',')
        }

        data.pollutantData.forEach(data => {
          let {factorId} = data

          switch (factorId) {
            case '1006':
              if (data.rootFactorId === 'H8O3') {
                factorId = '1006-8H'
              }
              break
            // case '1007':
            //   if (data.rootFactorId === 'PM10_24H') {
            //     factorId = '1007-24H'
            //   }
            //   break
            // case '1008':
            //   if (data.rootFactorId === 'PM2.5_24H') {
            //     factorId = '1008-24H'
            //   }
            //   break
          }

          newData[`${factorId}-value`] = Number(data.value.toFixed(3))
          newData[`${factorId}-iaqi`] = Math.round(data.iaqi)
        })
      })

      compositeIndexHourlies.forEach(data => {
        const dataTime = data.dataTime + ':00:00'
        const newData = dataMap[dataTime]
        const {value} = data

        if (Number.isFinite(value)) {
          if (Number.isFinite(newData.compositeIndex)) {
            newData.compositeIndex += data.value
          } else {
            newData.compositeIndex = data.value
          }
        }
      })

      const dataList = Object.values(dataMap).reverse()
      dataList.forEach(data => {
        if (Number.isFinite(data.compositeIndex)) {
          data.compositeIndex = Number(data.compositeIndex.toFixed(2))
        }
      })
      this.dataList = Object.freeze(dataList)
    },
    onExportBtnClick () {
      const {currentQuery} = this
      const {table} = this.$refs
      const wb = XLSXS.utils.table_to_book(table)
      const {SheetNames: [SheetName], Sheets} = wb
      const style = Object.freeze({
        alignment: {
          horizontal: 'center',
          vertical: 'center'
        },
        border: {
          color: {
            auto: 1
          },
          top: {
            style: 'thin'
          },
          right: {
            style: 'thin'
          },
          bottom: {
            style: 'thin'
          },
          left: {
            style: 'thin'
          }
        }
      })

      const rows = table.querySelectorAll('tr')
      const cells = [...rows[0].querySelectorAll('th, td')]
      const columnNum = cells.reduce((sum, cell) => sum + cell.colSpan, 0)
      const columnLetters = []

      for (let i = 1; i <= columnNum; i++) {
        let tmp = i
        let letter = ''

        do {
          let mod = tmp % 26
          mod = mod === 0 ? 26 : mod
          tmp -= mod
          letter = String.fromCharCode(64 + mod) + letter
        } while ((tmp /= 26) > 0)

        columnLetters.push(letter)
      }
      const sheet = Sheets[SheetName]
      const maxRowNum = Object.entries(sheet).reduce((maxRowNum, [key]) => {
        if (key[[0]] === '!') {
          return maxRowNum
        }

        const rowNum = key.match(/\d+/)[0] | 0
        return rowNum > maxRowNum ? rowNum : maxRowNum
      }, 0)

      const columnLettersLen = columnLetters.length

      for (let i = 0; i < columnLettersLen; i++) {
        const letter = columnLetters[i]

        for (let j = 1; j <= maxRowNum; j++) {
          const key = `${letter}${j}`
          const cell = sheet[key]

          if (cell) {
            cell.s = style
          } else {
            sheet[key] = {
              t: 's',
              v: '',
              s: style
            }
          }
        }
      }

      XLSXS.writeFile(wb, `AQI实时报-${this.filterSiteMap[currentQuery.siteIds[0]].name}-${currentQuery.beginTime.replace(/\//g, '-').replace(/[\s:]/g, '_')}-${currentQuery.endTime.replace(/\//g, '-').replace(/[\s:]/g, '_')}.xlsx`)
    }
  }
}
</script>

<style lang="scss" scoped>

</style>



// WEBPACK FOOTER //
// src/views/06Data-Management/02Normal/11AQIRealTimeReport/index.vue